import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './Lives.css';
import { api } from '../../api/client';
import type { Live as Performance } from '../../api/types';
import { PageHeader } from '../../components/PageHeader';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { Icon } from '../../components/Icon';

const Lives: React.FC = () => {
    const [performanceData, setPerformanceData] = useState<Performance[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        api.lives
            .list()
            .then((rows) => {
                if (!cancelled) setPerformanceData(rows);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message ?? 'Failed to load lives');
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const categorizePerformances = () => {
        const next: Performance[] = [];
        const past: Performance[] = [];

        performanceData.forEach(performance => {
            let dateToCompare = performance.date;

            // Handle TBC with fallback date format "TBC - 2025, December 16"
            if (performance.date.startsWith("TBC")) {
                const tbcParts = performance.date.split(' - ');
                if (tbcParts.length === 2) {
                    dateToCompare = tbcParts[1]; // Use the fallback date for comparison
                } else {
                    // Pure TBC without fallback date, assume it's upcoming
                    next.push(performance);
                    return;
                }
            }

            // Parse the date string format "2025, October 29"
            const dateStr = dateToCompare.trim();
            const parts = dateStr.split(', ');

            if (parts.length === 2) {
                const year = parseInt(parts[0]);
                const monthDay = parts[1].split(' ');
                const month = monthDay[0];
                const day = monthDay[1] ? parseInt(monthDay[1]) : 1;

                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                  'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNames.indexOf(month);

                if (monthIndex !== -1) {
                    const performanceDate = new Date(year, monthIndex, day);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // Set to start of today

                    if (performanceDate >= today) {
                        next.push(performance);
                    } else {
                        past.push(performance);
                    }
                } else {
                    // If we can't parse the date, assume it's upcoming
                    next.push(performance);
                }
            } else {
                // If format is unexpected, assume it's upcoming
                next.push(performance);
            }
        });

        const getDateForComparison = (performance: Performance) => {
            let dateStr = performance.date;
            if (performance.date.startsWith("TBC")) {
                const tbcParts = performance.date.split(' - ');
                dateStr = tbcParts.length === 2 ? tbcParts[1] : performance.date;
            }

            const parts = dateStr.split(', ');
            if (parts.length === 2) {
                const year = parseInt(parts[0]);
                const monthDay = parts[1].split(' ');
                const month = monthDay[0];
                const day = monthDay[1] ? parseInt(monthDay[1]) : 1;

                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                  'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNames.indexOf(month);

                if (monthIndex !== -1) {
                    return new Date(year, monthIndex, day);
                }
            }
            return new Date(0); // Fallback for unparseable dates
        };

        // Sort next performances by date (furthest first)
        next.sort((a, b) => {
            return getDateForComparison(b).getTime() - getDateForComparison(a).getTime();
        });

        // Sort past performances by date (most recent first)
        past.sort((a, b) => {
            return getDateForComparison(b).getTime() - getDateForComparison(a).getTime();
        });

        return { next, past };
    };

    const { next, past } = categorizePerformances();

    const renderPerformances = (performances: Performance[]) => (
        performances.map((performance, index) => {
            let displayDate = performance.date;
            let displayYear = "";

            if (performance.date.startsWith("TBC")) {
                const tbcParts = performance.date.split(' - ');
                if (tbcParts.length === 2) {
                    const fallbackParts = tbcParts[1].split(', ');
                    displayYear = fallbackParts[0]; // Extract year from fallback date
                    const monthDay = fallbackParts[1]?.split(' ');
                    if (monthDay && monthDay[0]) {
                        displayDate = `${monthDay[0]} - TBC`;
                    } else {
                        displayDate = "TBC";
                    }
                } else {
                    displayDate = "TBC";
                }
            } else {
                const parts = performance.date.split(', ');
                displayDate = parts[1];
                displayYear = parts[0];
            }

            return (
                <div key={index} className="performance-item">
                    <div className="performance-date">
                        <div className="date-main">
                            {displayDate}
                        </div>
                        {displayYear && (
                            <div className="date-year">
                                {displayYear}
                            </div>
                        )}
                    </div>
                    <div className="performance-details">
                        <span className="performance-title">
                            "{performance.title}"
                        </span>
                        <span className="performance-location">
                            <Icon icon={FaMapMarkerAlt} className="location-icon" />
                            {performance.location}
                        </span>
                    </div>
                </div>
            );
        })
    );

    return (
        <div className="lives-page">
            <Helmet>
                <title>Live Performances - Lorenzo Saini</title>
                <meta name="description" content="Upcoming and past live performances by Lorenzo Saini (Lo.Sai). Experience experimental electroacoustic music, live scoring, and multimedia performances." />
                <link rel="canonical" href="https://lorenzosaini.com/live" />
                <meta property="og:title" content="Live Performances - Lorenzo Saini" />
                <meta property="og:description" content="Upcoming and past live performances by Lorenzo Saini (Lo.Sai). Experience experimental electroacoustic music, live scoring, and multimedia performances." />
                <meta property="og:url" content="https://lorenzosaini.com/live" />
                <meta name="twitter:title" content="Live Performances - Lorenzo Saini" />
                <meta name="twitter:description" content="Upcoming and past live performances by Lorenzo Saini (Lo.Sai). Experience experimental electroacoustic music, live scoring, and multimedia performances." />
            </Helmet>
            <PageHeader
                title="LIVE"
            />
            <div className="lives-container">
                <div className="performances-list">
                    {error && <p className="section-marker">{error}</p>}
                    {next.length > 0 && (
                        <div className="section">
                            <div className="section-marker">NEXT</div>
                            {renderPerformances(next)}
                        </div>
                    )}

                    {past.length > 0 && (
                        <div className="section">
                            <div className="section-marker">PAST</div>
                            {renderPerformances(past)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Lives;